import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getCampaignListConfig } from '../config/campaignListConfig';
import {
  exportEmailCampaignsToXlsx,
  exportSmsCampaignsToXlsx,
} from '../utils/campaignExport';

export function useCampaignListDashboard(channel, { setSuccessMessage, setErrorMessage } = {}) {
  const navigate = useNavigate();
  const config = getCampaignListConfig(channel);
  const isEmail = channel === 'email';

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('table');
  const [filters, setFilters] = useState(config.initialFilters);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const [openAnalyticsDialog, setOpenAnalyticsDialog] = useState(false);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [openDeleteConfirmDialog, setOpenDeleteConfirmDialog] = useState(false);

  const fetchCampaigns = useCallback(async () => {
    try {
      const response = await axios.get(config.listUrl);
      const rows = (response.data || []).map(config.normalizeCampaign);
      setCampaigns(rows);
    } catch (error) {
      console.error(`Failed to fetch ${channel} campaigns:`, error);
    } finally {
      setLoading(false);
    }
  }, [config, channel]);

  const refreshAll = useCallback(async () => {
    await fetchCampaigns();
  }, [fetchCampaigns]);

  useEffect(() => {
    refreshAll();
    const interval = setInterval(() => {
      if (!openAnalyticsDialog) {
        refreshAll();
      }
    }, config.refreshIntervalMs);
    return () => clearInterval(interval);
  }, [refreshAll, openAnalyticsDialog, config.refreshIntervalMs]);

  const filteredCampaigns = useMemo(
    () => campaigns.filter((c) => config.matchesFilters(c, filters)),
    [campaigns, filters, config],
  );

  const selectedCampaign = useMemo(
    () => campaigns.find((c) => c._id === selectedCampaignId) || null,
    [campaigns, selectedCampaignId],
  );

  const deleteCount =
    selectedCampaigns.length > 0
      ? selectedCampaigns.length
      : selectedCampaignId
        ? 1
        : 0;

  const handleRowClick = (campaignId) => {
    const campaign = campaigns.find((c) => c._id === campaignId);
    if (campaign?.status === 'draft') {
      navigate(
        channel === 'sms'
          ? `/sms/campaign?draft=${campaignId}`
          : `/campaigns?draft=${campaignId}`,
      );
      return;
    }
    setSelectedCampaignId(campaignId);
    setOpenAnalyticsDialog(true);
  };

  const handleMenuOpen = (event, campaignId) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedCampaignId(campaignId);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const toggleCampaignSelection = (id) => {
    setSelectedCampaigns((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const handleSelectAllOnPage = (filteredOnPage, checked) => {
    if (checked) {
      const ids = filteredOnPage.map((n) => n._id);
      setSelectedCampaigns((prev) => [...new Set([...prev, ...ids])]);
      return;
    }
    setSelectedCampaigns((prev) =>
      prev.filter((id) => !filteredOnPage.some((c) => c._id === id)),
    );
  };

  const handleDeleteMenuClick = () => {
    if (selectedCampaignId && !selectedCampaigns.includes(selectedCampaignId)) {
      setSelectedCampaigns([selectedCampaignId]);
    }
    setOpenDeleteConfirmDialog(true);
    handleMenuClose();
  };

  const handleConfirmDelete = async () => {
    let idsToDelete = [...selectedCampaigns];
    if (idsToDelete.length === 0 && selectedCampaignId) {
      idsToDelete = [selectedCampaignId];
    }
    if (idsToDelete.length === 0) {
      setErrorMessage?.('No campaigns selected for deletion.');
      setOpenDeleteConfirmDialog(false);
      return;
    }
    try {
      await axios.delete(config.deleteUrl, { data: { ids: idsToDelete } });
      setCampaigns((prev) => prev.filter((c) => !idsToDelete.includes(c._id)));
      setSelectedCampaigns([]);
      setSuccessMessage?.(
        `${idsToDelete.length} ${config.deleteItemLabel}(s) deleted successfully`,
      );
    } catch (error) {
      console.error('Failed to delete campaigns:', error);
      setErrorMessage?.(
        error.response?.data?.error || 'Failed to delete campaign(s). Please try again.',
      );
    }
    setOpenDeleteConfirmDialog(false);
    setSelectedCampaignId(null);
  };

  const exportCampaigns = async () => {
    const toExport = selectedCampaigns.length
      ? campaigns.filter((c) => selectedCampaigns.includes(c._id))
      : campaigns;
    if (isEmail) {
      await exportEmailCampaignsToXlsx(toExport);
    } else {
      exportSmsCampaignsToXlsx(toExport);
    }
  };

  const getStatus = (campaign) => config.getStatus(campaign);

  return {
    config,
    campaigns,
    setCampaigns,
    loading,
    viewMode,
    setViewMode,
    filters,
    setFilters,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    menuAnchor,
    selectedCampaignId,
    selectedCampaign,
    openAnalyticsDialog,
    setOpenAnalyticsDialog,
    selectedCampaigns,
    openDeleteConfirmDialog,
    setOpenDeleteConfirmDialog,
    filteredCampaigns,
    deleteCount,
    handleRowClick,
    handleMenuOpen,
    handleMenuClose,
    toggleCampaignSelection,
    handleSelectAllOnPage,
    handleDeleteMenuClick,
    handleConfirmDelete,
    exportCampaigns,
    getStatus,
    refreshAll,
    fetchCampaigns,
  };
}
